import Input, { CheckboxInput } from "@/components/Global/Input";
import { useFormik } from "formik";
import "react-datepicker/dist/react-datepicker.css";
import "@/styles/picker.css";
import { vendorSchema } from "@/schema";
import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import PrimaryBtn, { SecondaryBtn } from "@/components/Global/Button";
import PhoneNumber from "@/components/Global/PhoneNumber";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  serverDELETE,
  serverGET,
  serverPOST,
  serverPUT,
} from "@/utils/HttpFunctions";
import { errorNotfiy, successNotfiy } from "@/components/Global/Toast";
import Dropdown from "@/components/Global/Dropdown";

import { IoIosLink } from "react-icons/io";
import { AiOutlineDelete } from "react-icons/ai";
import Modal from "@/components/Global/Modal";
import Warning from "@/assets/exclamation.png";
import BreadCrumb from "@/components/Global/BreadCrumb";
import { useIsScrollable } from "@/utils/hooks/useIsScrollable";
import TextArea from "@/components/Global/TextArea";
import { HiOutlineMail } from "react-icons/hi";
import {
  handlePreventSubmitForm,
  RemoveAlphabet,
} from "@/utils/CommonFunctions";
import usePermissions from "@/utils/hooks/usePermissions";
import { usePrompt } from "@/utils/hooks/usePrompt";
import useDropdownData from "@/utils/hooks/useDrodownData";
import { MdOutlinePercent } from "react-icons/md";
import QuickbooksModal from "../../components/Quickbooks/QuickbooksModal";
import QuickbooksStatusChip from "../../components/Quickbooks/QuickbooksStatusChip";
import useUserStore from "@/utils/Stores/userStore";
import { quickBooksStatusValue } from "@/utils/constants";
import useWindowSize from "@/utils/hooks/useWindowSize";
import CreateVendorMobile from "./CreateVendor.mobile";
type inputTypes = {
  errors: any;
  touched: any;
  values: any;
  handleChange: any;
  handleSubmit: any;
  setFieldValue: any;
  setFieldError?: any;
  isVendor?: boolean;
  isClient?: boolean;
};
export default function CreateVendor({
  isPopup,
  handleClose,
  initialData,
  handleSelectVendor,
  isClient,
}: {
  isPopup?: boolean;
  handleClose?: any;
  initialData?: any;
  handleSelectVendor?: (e) => void;
  isClient?: boolean;
}) {
  const { isMobile } = useWindowSize();
  const { id } = useParams();
  const [fetchedData, setFetchedData] = useState(null);
  const [deleteError, setDeleteError] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const permissions = usePermissions("Manage", "Vendor");
  const readOnly = !permissions?.write;
  const [readOnlyError, setReadOnlyError] = useState(false);
  const [quickbooksSyncedModal, setQuickbooksSyncedModal] = useState(false);
  const user = useUserStore((state: any) => state.user);

  const navigate = useNavigate();

  const { data } = useQuery({
    queryKey: ["vendor", id], // Unique query key
    queryFn: () => serverGET(`/vendors/` + id), // Query function
    enabled: !!id && !isPopup, // Only run the query if id is truthy
  });

  const { data: purchaseOrderData, refetch } = useQuery({
    queryKey: ["purchase-order", id], // Unique query key
    queryFn: () => serverGET(`/purchaseorders/vendor/` + id), // Query function
    enabled: false,
  });

  const isScrollable = useIsScrollable();

  useEffect(() => {
    if (purchaseOrderData) {
      if (purchaseOrderData?.is_purchase_attached) {
        setDeleteError(
          "A PO  is attached to this vendor hence it cannot be deleted."
        );
      } else {
        setShowDeleteModal(true);
      }
    }
  }, [purchaseOrderData]);

  useEffect(() => {
    if (data) {
      setFetchedData({
        ...data,
        tags: data.tags.map((tag) => ({
          value: tag,
          label: tag.charAt(0)?.toUpperCase() + tag.slice(1),
        })),
        address:
          data?.address?.length > 0
            ? data?.address
            : [
                {
                  address_title: "",
                  address: "",
                  city: "",
                  state: "",
                  zip: "",
                  isPrimary: true,
                },
              ],
        contact:
          data?.contact?.length > 0
            ? data.contact
            : [
                {
                  email: "",
                  phone_number: "",
                  isPrimary: true,
                },
              ],
      });
    }
  }, [data]);

  // Define default values
  const defaultValues = {
    name: "",
    company: "",
    contact: [{ email: "", phone_number: "", isPrimary: true }],
    // email: "",
    // phone_numbers: [""],
    tags: [],
    notes: "",
    website_link: "",
    address: [
      {
        address_title: "",
        address: "",
        city: "",
        state: "",
        zip: "",
        isPrimary: true,
      },
    ],
    vendorDiscount: { value: "", unit: "%" },
  };

  const handleToastClose = () => {
    if (isPopup) {
      handleClose();
    } else {
      navigate("/vendors");
    } // Replace "/desired-path" with your actual path
  };

  const handleMutate = () => {
    if (isPopup) {
      handleClose();
    } else {
      handleMutationSuccess("/vendors");
    }
  };

  // Merge fetched data with default values, preferring fetched data
  const initialValues = initialData
    ? { ...defaultValues, ...initialData }
    : fetchedData
      ? {
          ...defaultValues,
          ...fetchedData,
        }
      : defaultValues;

  const { mutate: updateMutate, isPending: updatePending } = useMutation({
    mutationFn: (body: any) =>
      serverPUT("/vendors/" + (initialData?._id || id), body),
    onError: (error, variables, context) => {
      errorNotfiy(
        error.message || "Something went wrong while updating vendor detail"
      );
    },
    onSuccess: (data, variables, context) => {
      if (variables.is_delete) {
        successNotfiy("Success: Vendor is deleted", () => {});
        handleToastClose();
      } else {
        if (data?.duplicate) {
          errorNotfiy(data?.message);
          return;
        }

        successNotfiy("Success: Vendor detail is updated", () => {});
        handleMutate();

        if (handleSelectVendor) {
          handleSelectVendor(data);
        }
      }
    },
  });

  const { mutate: createMutate, isPending } = useMutation({
    mutationFn: (body) => serverPOST("/vendors/create", body),
    onError: (error, variables, context) => {
      errorNotfiy(
        error.message || "Something went wrong while creating new vendor"
      );
    },
    onSuccess: (data, variables, context) => {
      if (data?.duplicate) {
        errorNotfiy(data?.message);
        return;
      }

      if (handleSelectVendor) {
        handleSelectVendor(data);
      }

      successNotfiy("Success: New Vendor is added", () => {});
      handleMutate();
    },
  });

  const { mutate: deleteMutate, isPending: deletePending } = useMutation({
    mutationFn: (body) => serverDELETE("/vendors/" + id),
    onError: (error, variables, context) => {
      errorNotfiy(
        error.message || "Something went wrong while deleting vendor"
      );
    },
    onSuccess: (data, variables, context) => {
      successNotfiy("Success: Vendor is deleted", () => {});
      handleMutate();
    },
  });

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: vendorSchema,
    enableReinitialize: true,

    onSubmit: async (values, { setSubmitting, setErrors }) => {
      // Check if both email and phone numbers are empty
      if (values.contact.some((num) => !num.email && !num.phone_number)) {
        setErrors({
          "email-or-phone": "Please provide either an email or phone number.",
        });
        setSubmitting(false);
        return;
      }

      const hasPrimary = values.address.some((addr) => addr.isPrimary);
      const isAddress = values.address.some(
        (addr) =>
          addr?.address_title?.trim() ||
          addr?.address?.trim() ||
          addr?.city?.trim() ||
          addr?.state?.trim() ||
          addr?.zip?.trim()
      );
      if (!hasPrimary && isAddress && values.address.length > 0) {
        values.address = values.address.map((addr, index) => ({
          ...addr,
          isPrimary: index === 0,
        }));
      }
      if (!isAddress) {
        values.address = [];
      }
      const hasContactPrimary = values.contact.some((addr) => addr.isPrimary);

      if (!hasContactPrimary && values.contact.length > 0) {
        values.contact = values.contact.map((addr, index) => ({
          ...addr,
          isPrimary: index === 0,
        }));
      }

      // parsing vendor discount value to number form
      values = {
        ...values,
        vendorDiscount: {
          ...values.vendorDiscount,
          value: +values.vendorDiscount?.value,
        },
        tags: values.tags.map((tag) => tag.value),
      };

      try {
        if (user?.quickbooks?.realmId && !quickbooksSyncedModal) {
          setQuickbooksSyncedModal(true);
          return;
        }
        setQuickbooksSyncedModal(false);
        if (isPopup && !initialData) {
          createMutate({
            ...values,
          });
        } else if (id) {
          updateMutate({
            ...values,
          });
        } else {
          createMutate({
            ...values,
          });
        }
        // res.status(200).json(data);
      } catch (error) {
        console.log("er", error);
        // res.status(500).json({ error: error.message });
      }
    },
  });

  const handleActive = async () => {
    // const body = {
    //   is_active: !fetchedData?.is_active,
    //   is_delete: true,
    // };
    // updateMutate(body);
    deleteMutate();
  };

  const handleDelete = async () => {
    if (purchaseOrderData) {
      if (purchaseOrderData?.is_purchase_attached) {
        setDeleteError(
          "A project  is associated to this client hence it cannot be deleted."
        );
      } else {
        setShowDeleteModal(true);
      }
      return;
    }
    refetch();
  };

  // Deconstruct Formik object
  const { handleSubmit, dirty } = formik;
  const { PromptModal, handleMutationSuccess } = usePrompt(
    "You have unsaved changes. Are you sure you want to leave?",
    dirty
  );

  if (isMobile) {
    return <CreateVendorMobile data={data || {}} />;
  }
  return (
    <div className="relative">
      <div className={`${isPopup ? "w-full" : "w-11/12"} mx-auto mt-3`}>
        {readOnlyError && (
          <>
            {errorNotfiy("You have the permission to view only", () =>
              setReadOnlyError(false)
            )}
          </>
        )}
        {!isPopup && (
          <BreadCrumb
            data={[
              { name: "Vendor", link: `/Vendors` },
              { name: id ? "Edit Vendor" : "Add New Vendor", link: `#` },
            ]}
          />
        )}

        {quickbooksSyncedModal && (
          <QuickbooksModal
            handleSubmit={handleSubmit}
            setQuickbooksSyncedModal={setQuickbooksSyncedModal}
            values={formik.values}
            title="vendor"
            disabled={isPending || updatePending}
            setFieldValue={formik.setFieldValue}
          />
        )}
        <PromptModal />

        <form
          onSubmit={handleSubmit}
          onKeyDown={handlePreventSubmitForm}
          className={"relative "}
        >
          {readOnly && (
            <div
              className="absolute inset-0 bg-transparent z-50"
              onClick={() => setReadOnlyError(true)}
            />
          )}
          <div className="my-4 flex justify-between items-center">
            {!isPopup ? (
              <div>
                <p className="text-h1 text-black-secondary text-left">
                  {" "}
                  {id ? "Edit Vendor" : "Add New Vendor"}
                </p>
                <p className="font-semibold text-lg text-black-primary text-left pt-4">
                  {" "}
                  Vendor Details
                </p>
                <span className="text-gray-600 font-normal text-sm">
                  {!id &&
                    "Give your vendor a name and fill in the necessary details."}
                </span>
              </div>
            ) : null}

            {!readOnly && (
              <div className="flex gap-3 items-center">
                {/* {!!id && !isPopup && (
                <FiArchive
                  className="text-customGrey-icon w-[17px] h-[17px] cursor-pointer"
                  onClick={() => handleActive()}
                />
              )} */}

                {user?.quickbooks?.realmId && (
                  <QuickbooksStatusChip
                    status={
                      !data?._id
                        ? quickBooksStatusValue.NOT_READY
                        : data?.quickbooks_data?.id
                          ? quickBooksStatusValue.COMPLETED
                          : quickBooksStatusValue.NOT_SYNC
                    }
                    title="vendor"
                  />
                )}

                {!!id && !isPopup && (
                  <AiOutlineDelete
                    className="text-customGrey-icon w-5 h-5 cursor-pointer"
                    onClick={() => handleDelete()}
                  />
                )}
                {!isPopup && (
                  <>
                    <SecondaryBtn
                      text="Cancel"
                      onClick={() => handleToastClose()}
                    />
                    <PrimaryBtn
                      type="submit"
                      text="Save"
                      disabled={isPending || updatePending}
                      loading={isPending || updatePending}
                    />
                  </>
                )}
              </div>
            )}
          </div>
          <hr />
          <div className="flex flex-col gap-4 mt-6">
            <div
              className={`grid ${
                isPopup ? " grid-cols-3 " : " grid-cols-4 "
              } gap-4`}
            >
              {!isPopup && (
                <div>
                  <span className="block text-left text-sm font-semibold text-black-secondary">
                    Contact Details
                  </span>
                </div>
              )}
              <div className="col-span-3">
                <Contact {...formik} vendor={true} isPopup={isPopup} />
                {formik.errors["email-or-phone"] && (
                  <div className="text-sm text-red-500 mt-2">
                    Either email or phone number is required
                  </div>
                )}
              </div>
            </div>
            <hr />

            <div
              className={`grid ${
                isPopup ? " grid-cols-3 " : " grid-cols-4 "
              }   gap-4`}
            >
              {!isPopup && (
                <div>
                  <span className="block text-left text-sm font-semibold text-black-secondary">
                    Address
                  </span>
                </div>
              )}
              <div className="col-span-3">
                <Address {...formik} isPopup={isPopup} isVendor={true} />
              </div>
            </div>
            <hr />
            <div
              className={`grid ${
                isPopup ? " grid-cols-3 " : " grid-cols-4 "
              }   gap-4`}
            >
              {!isPopup && (
                <div>
                  <span className="block text-left text-sm font-semibold text-black-secondary">
                    Tags & Notes
                  </span>
                </div>
              )}
              <div className="col-span-3">
                <TagsAndNotes {...formik} isVendor={true} />
              </div>
            </div>
          </div>
          {isPopup && (
            <div className="flex gap-3 mt-6 items-center justify-end">
              <SecondaryBtn
                text="Cancel"
                onClick={() => handleToastClose()}
                fullWidth
              />
              <PrimaryBtn
                text="Save"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleSubmit(e);
                }}
                disabled={updatePending || isPending}
                loading={updatePending || isPending}
                fullWidth
              />
            </div>
          )}
          {isScrollable && !readOnly && !isPopup && (
            <div className="flex gap-3 my-6 items-center justify-end">
              {!!id && !isPopup && (
                <AiOutlineDelete
                  className="text-customGrey-icon w-5 h-5 cursor-pointer"
                  onClick={() => handleDelete()}
                />
              )}
              <SecondaryBtn text="Cancel" onClick={() => handleToastClose()} />
              <PrimaryBtn
                type="submit"
                text="Save"
                disabled={updatePending || isPending}
                loading={updatePending || isPending}
              />
            </div>
          )}{" "}
        </form>
        {!!deleteError && (
          <Modal
            handleClose={() => setDeleteError("")}
            title={"Delete vendor"}
            description={
              "A purchase order is associated with this vendor and so it cannot be deleted. Please use the archive option instead."
            }
            icon={Warning}
            width={"max-w-sm"}
            central={true}
            leftAlign={true}
            inLine={true}
          >
            <div className="w-full mt-6">
              <SecondaryBtn
                text="Okay"
                fullWidth={true}
                onClick={() => setDeleteError("")}
              />
            </div>
          </Modal>
        )}
        {showDeleteModal && (
          <Modal
            handleClose={() => setShowDeleteModal(false)}
            title={"Delete vendor"}
            description={
              "Are you sure you want to delete this vendor? This action cannot be undone."
            }
            icon={Warning}
            width={"max-w-sm"}
            leftAlign={true}
            central={true}
            inLine
          >
            <div className="w-full flex gap-x-4 mt-6">
              <SecondaryBtn
                text="Cancel"
                fullWidth={true}
                onClick={() => setShowDeleteModal(false)}
              />
              <PrimaryBtn
                text="Delete"
                disabled={deletePending}
                loading={deletePending}
                fullWidth={true}
                color="bg-customRed-bg"
                onClick={handleActive}
              />
            </div>
          </Modal>
        )}
      </div>
    </div>
  );
}

export function Contact({
  errors,
  touched,
  values,
  handleChange,
  setFieldValue,
  setFieldError,
  isPopup,
  vendor,
  isClient,
}: inputTypes & { vendor?: boolean; isPopup?: boolean; isClient?: boolean }) {
  const handlePhoneChange = (val, index, name = "") => {
    // if (vendor) {
    setFieldValue(`contact.${index}.${name}`, val || "");
    // } else {
    //   setFieldValue(`phone_numbers.${index}`, val || "");
    // }

    // if (field === "number") {
    //   setFieldValue(`phone_numbers.${index}`, val || "");
    // } else if (field === "email") {
    //   setFieldValue(`email.${index}`, val || "");
    // }
  };

  const addPhone = () => {
    // if (vendor) {
    const newItems = [...values.contact, { email: "", phone_number: "" }];
    setFieldValue("contact", newItems);
    // } else {
    //   const newItems = [...values.phone_numbers, ""];
    //   setFieldValue("phone_numbers", newItems);
    // }

    //  const newEmail =[...values.email, ""]s
    // setFieldValue("email", newEmail);
  };

  const handleDeletePhone = (indexDelete) => {
    // if (vendor) {
    setFieldValue(
      "contact",
      values.contact.filter((item, index) => index != indexDelete)
    );
    // } else {
    //   setFieldValue(
    //     "phone_numbers",
    //     values.phone_numbers.filter((item, index) => index != indexDelete)
    //   );
    // }

    // setFieldValue(
    //   "email",
    //   values.email.filter((item, index) => index != indexDelete)
    // );
  };

  const handleCheckChange = (e: any) => {
    const index = parseInt(e.target.name.split("-")[1]);
    let temp = [...values.contact];
    const updatedContacts = temp.map((addr, i) => ({
      ...addr,
      isPrimary: i === index,
    }));
    setFieldValue("contact", updatedContacts);
  };

  return (
    <div className="grid grid-cols-2 gap-4">
      <div>
        <Input
          label="Company Name"
          name="company"
          type="text"
          required={vendor}
          placeholder="Company"
          value={values.company}
          onChange={handleChange}
          error={errors.company}
          touched={touched.company}
          // required={vendor}
        />
      </div>
      <div>
        <Input
          label="Contact Name"
          name="name"
          type="text"
          placeholder="Name"
          value={values.name}
          onChange={handleChange}
          error={errors.name}
          touched={touched.name}
          required={!vendor}
          autoComplete="clientVendorName"
        />
      </div>
      {vendor && (
        <div>
          <Input
            label="Website"
            name="website_link"
            type="text"
            placeholder="Website Link"
            value={values.website_link}
            onChange={handleChange}
            error={errors.website_link}
            touched={touched.website_link}
            icon={<IoIosLink className="text-gray-500" size={17} />}
          />
        </div>
      )}

      {vendor && (
        <div>
          <Input
            label="Account Number #"
            name="account_number"
            type="text"
            placeholder="Account Number"
            value={values.account_number}
            onChange={handleChange}
            error={errors.account_number}
            touched={touched.account_number}
          />
        </div>
      )}

      {/* {!vendor && (
        <>
          <div>
            <span className="block text-sm text-black-secondary font-medium text-left mb-1">
              Phone Number
              {isClient && (
                <span className="text-xs text-error-500 ml-1">*</span>
              )}
            </span>

            <div className="flex flex-col gap-2">
              {values.phone_numbers.map((phone, index) => {
                return (
                  <>
                    <div className={`flex w-full items-center gap-x-2  mb-2`}>
                      <div className="flex-1">
                        <PhoneNumber
                          phone={phone}
                          handleChange={(value) =>
                            handlePhoneChange(value, index)
                          }
                          error={errors.phone_numbers?.[index]}
                          touched={touched.phone_numbers?.[index]}
                          autoComplete="clientVendorPhone"
                        />
                      </div>

                      <div
                        className={` text-customRed-lightLabel  cursor-pointer ${
                          values.phone_numbers?.length == 1
                            ? " hidden "
                            : " visible"
                        } ${errors.phone_numbers?.[index] ? "-mt-6" : ""}`}
                      >
                        <AiOutlineDelete
                          className="size-5 "
                          onClick={() => {
                            handleDeletePhone(index);
                          }}
                        />
                      </div>
                    </div>
                  </>
                );
              })}
            </div>
            <button
              type="button"
              onClick={addPhone}
              className="text-sm text-customGreen-text font-medium"
            >
              Add Additional Contact
            </button>
          </div>
          <div className="">
            <Input
              label="Email"
              key={`email`}
              name={`email`}
              type="email"
              placeholder="Email"
              value={values.email}
              onChange={handleChange}
              error={errors.email}
              touched={touched.email}
              autoComplete="clientVendorEmail"
              icon={<HiOutlineMail className="text-gray-500 w-[17px] h-auto" />}
              required
            />
          </div>
        </>
      )} */}

      {/* {vendor && ( */}
      <div className="col-span-2">
        {values.contact.map((item, index) => {
          return (
            <div
              className={` relative grid grid-cols-2 gap-4 mb-4 border border-customGrey-border2 rounded-lg p-4`}
              key={index}
            >
              <div className="w-full">
                <span className="text-sm text-black-secondary font-medium flex mb-2">
                  Phone Number
                </span>
                <PhoneNumber
                  phone={item.phone_number}
                  handleChange={(value) =>
                    handlePhoneChange(value, index, "phone_number")
                  }
                  error={errors.contact?.[index]?.phone_number}
                  touched={touched.contact?.[index]?.phone_number}
                />
              </div>
              <div className=" w-full">
                <Input
                  label="Email"
                  key={`contact.${index}.email`}
                  name={`contact.${index}.email`}
                  type="email"
                  placeholder="Email"
                  value={item.email}
                  onChange={(e) =>
                    handlePhoneChange(e.target.value, index, "email")
                  }
                  error={errors.contact?.[index]?.email}
                  touched={touched.contact?.[index]?.email}
                  autoComplete="clientVendorEmail"
                  icon={
                    <HiOutlineMail className="text-gray-500 w-[17px] h-auto" />
                  }
                />
              </div>
              <div className={"block"}>
                <label className="inline-flex items-center ">
                  <CheckboxInput
                    onChange={handleCheckChange}
                    label="Mark as primary"
                    checked={item.isPrimary}
                    name={`primary-${index}`}
                  />
                </label>
              </div>

              <div
                className={`absolute -right-3 -top-7 text-customRed-lightLabel cursor-pointer bg-error-100 rounded-full w-6 h-6 flex items-center justify-center ${
                  values.contact.length == 1
                    ? " hidden "
                    : " visible flex items-center mt-5"
                }`}
              >
                <AiOutlineDelete
                  onClick={() => {
                    handleDeletePhone(index);
                  }}
                />
              </div>
            </div>
          );
        })}
        <button
          type="button"
          onClick={addPhone}
          className="text-sm text-customGreen-text font-medium"
        >
          Add Additional Contact
        </button>
      </div>
      {/* )} */}
    </div>
  );
}

export function Address({
  errors,
  touched,
  values,
  setFieldValue,
  isProject,
  selectedBilling,
  setSelectedBilling,
  isPopup,
  isVendor,
}: inputTypes & {
  isProject?: Boolean;
  selectedBilling?: any;
  setSelectedBilling?: any;
  isPopup?: boolean;
}) {
  const addAddress = () => {
    setFieldValue("address", [
      ...values.address,
      { address_title: "", address: "", city: "", state: "", zip: "" },
    ]);
  };

  const customHandleChange = (e: any) => {
    let temp = [...values.address];
    temp[parseInt(e.target.name.split("-")[1])][e.target.name.split("-")[0]] =
      e.target.value;
    setFieldValue("address", temp);
  };

  const handleCheckChange = (e: any) => {
    const index = parseInt(e.target.name.split("-")[1]);
    let temp = [...values.address];
    const updatedAddresses = temp.map((addr, i) => ({
      ...addr,
      isPrimary: i === index,
    }));
    setFieldValue("address", updatedAddresses);
  };

  const handleDeleteAddress = (index: number) => {
    setFieldValue(
      "address",
      values.address?.filter((_, i) => i !== index)
    );
    if (selectedBilling === index) {
      setSelectedBilling("shippingAddress");
    }
  };
  // console.log(values["address"]);
  return (
    <div>
      {values?.address?.map((item: any, index: number) => (
        <div
          className="relative border border-customGrey-border2 rounded-lg p-4 mb-4"
          key={index}
        >
          <div
            className={`absolute -right-2 -top-2 text-customRed-lightLabel cursor-pointer bg-error-100 rounded-full w-6 h-6 flex items-center justify-center ${
              values.address.length == 1 && !isProject
                ? " hidden "
                : " visible  size-5"
            }`}
          >
            <AiOutlineDelete
              onClick={() => {
                handleDeleteAddress(index);
              }}
            />
          </div>
          <div className=" relative grid grid-cols-4 gap-4 mb-3">
            <div>
              <Input
                label="Name"
                name={`address_title-${index}`}
                type="text"
                placeholder="Name"
                required={!isVendor}
                value={item.address_title}
                onChange={customHandleChange}
                error={errors?.address?.[index]?.address_title}
                touched={touched?.address?.[index]?.address_title}
                autoComplete="name"
              />
            </div>
            <div className="col-span-3">
              <Input
                label="Address Line"
                name={`address-${index}`}
                type="text"
                placeholder="Address Line"
                value={item.address}
                required={!isVendor}
                onChange={customHandleChange}
                error={errors?.address?.[index]?.address}
                touched={touched?.address?.[index]?.address}
                autoComplete="street-address"
              />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4 mb-3">
            <div>
              <Input
                label="City"
                name={`city-${index}`}
                type="text"
                placeholder="City"
                value={item.city}
                onChange={customHandleChange}
                error={errors.city}
                touched={touched.city}
              />
            </div>
            <div>
              <Input
                label="State"
                name={`state-${index}`}
                type="text"
                placeholder="State"
                value={item.state}
                onChange={customHandleChange}
                error={errors.state}
                touched={touched.state}
              />
            </div>
            <div>
              <Input
                label="Zip"
                name={`zip-${index}`}
                type="text"
                placeholder="Zip"
                value={item.zip}
                onChange={customHandleChange}
                error={errors.zip}
                touched={touched.zip}
              />
            </div>
            {/* {!isPopup && (
              <div
                className={` text-customRed-lightLabel absolute left-[21%] -top-[21%] cursor-pointer ${
                  values.address.length == 1 ? " hidden " : " visible"
                }`}
              >
                <AiOutlineDelete
                  onClick={() => {
                    handleDeleteAddress(index);
                  }}
                />
              </div>
            )} */}
          </div>
          <div className="flex gap-x-2 items-center justify-between">
            {isProject ? (
              <div>
                <CheckboxInput
                  onChange={(e) => {
                    setSelectedBilling(index);
                  }}
                  label="Use this as a billing address"
                  checked={selectedBilling === index}
                  name={`address`}
                />
              </div>
            ) : (
              <div className={"block"}>
                <label className="inline-flex items-center ">
                  <CheckboxInput
                    onChange={handleCheckChange}
                    label="Mark as primary"
                    checked={item.isPrimary}
                    name={`primary-${index}`}
                  />
                </label>
              </div>
            )}
          </div>
        </div>
      ))}
      <button
        type="button"
        className={`text-sm font-medium text-customGreen-text ${isProject ? "mt-0" : "mt-4"}`}
        onClick={() => addAddress()}
      >
        Add Additional Address
      </button>
    </div>
  );
}

export function TagsAndNotes({
  errors,
  touched,
  values,
  handleChange,
  setFieldValue,
  isVendor,
  isClient,
}: inputTypes) {
  const { data = [], mutate, isPending } = useDropdownData();

  const handleDiscountChange = (e) => {
    let { name, value } = e.target;
    const prevValues = { ...values };
    const [field, subField] = name.split(".");
    if (subField != "unit") {
      value = RemoveAlphabet(value);
    }
    setFieldValue([field], { ...prevValues[field], [subField]: value });
  };

  const handleDropdownCreateNew = (inputValue: string, identifier: string) => {
    if (!inputValue.trim()) return;
    mutate({
      value: inputValue,
      label: inputValue,
      identifier,
    } as any);
  };

  return (
    // <form onSubmit={handleSubmit}>
    <div className="grid grid-cols-2 gap-4 ">
      <div className="space-y-4">
        <Dropdown
          placeholder="Tags"
          label="Tags"
          isMulti={true}
          options={isClient ? data?.clientTags : data?.vendorTags}
          value={values.tags}
          handleChange={(e) => setFieldValue("tags", e)}
          isCreateNew={true}
          onCreateNew={(inputValue) => {
            handleDropdownCreateNew(
              inputValue,
              isClient ? "clientTags" : "vendorTags"
            );
            if (!inputValue.trim()) return;
            setFieldValue("tags", [
              ...values.tags,
              { value: inputValue, label: inputValue },
            ]);
          }}
          createNewLabel="Add new tag"
          isLoading={isPending}
        />
        {isVendor && (
          <div>
            <Input
              name="vendorDiscount.value"
              value={values.vendorDiscount?.value}
              onChange={handleDiscountChange}
              label={"Vendor Discount"}
              placeholder={"00.00"}
              type={"text"}
              rightIcon={<MdOutlinePercent />}
            />
            {/* <span className="text-xs text-slate-700 mb-2 flex">

              Vendor Discount
            </span>
            <div className="flex items-center border text-sm border-gray-300 rounded-md overflow-hidden h-[43px]">
              <input
                type="text"
                value={values.vendorDiscount?.value}
                onChange={handleDiscountChange}
                name="vendorDiscount.value"
                placeholder="00.00"
                className="p-2 w-full border-none focus:ring-0 focus:outline-none text-base placeholder:text-base placeholder:text-gray-500"
              />

              <select
                value={values.vendorDiscount?.unit}
                onChange={handleDiscountChange}
                name="vendorDiscount.unit"
                className="p-2 border-none focus:ring-0 focus:outline-none text-end"
              >
                <option value="%">%</option>
                <option value="curr">$</option>
              </select>
            </div>
            {touched && errors && <p className="text-sm text-red-500">{errors.vendorDiscount?.}</p>} */}
          </div>
        )}
      </div>
      <div>
        <TextArea
          value={values.notes}
          label="Notes"
          placeholder="Add notes here"
          name="notes"
          error={errors.notes}
          touched={touched.notes}
          onChange={handleChange}
          maxLength={275}
          height="h-44"
        />
      </div>
      {/* 
      <button
        type="submit"
        className={`px-8 py-2 rounded-md text-sm hover:bg-green-600 text-white  bg-green-400  disabled:bg-green-100 `}
      >
        Submit
      </button> */}
    </div>
    // </form>
  );
}
